/**
 * DeleteStrategyModal — soft-delete a user strategy (SHA-43 item 2).
 *
 * Calls DELETE /api/user-strategies/:id (userId-scoped, cascades soft-delete
 * to strategy_configs / backtest_runs / trades_v2 / optimization runs that
 * reference this strategy — see src/data/user-strategies.ts deleteUserStrategy).
 *
 * Warns the user up-front when the strategy has configs so they understand
 * the cascade before confirming — this does not block deletion (soft-delete
 * is reversible at the DB layer), it just makes the blast radius visible.
 */

import { useState } from 'react';
import { Modal } from '../Modal/Modal.js';
import { useDeleteUserStrategy } from '../../hooks/useUserStrategies.js';

interface DeleteStrategyModalProps {
  isOpen: boolean;
  strategyId: string;
  strategyName: string;
  /** Number of configurations (manual + grid-search) that reference this strategy. */
  configCount: number;
  onClose: () => void;
  /** Called only when the delete succeeds (in addition to onClose) — e.g. to clear a selection. */
  onDeleted?: () => void;
}

export function DeleteStrategyModal({
  isOpen,
  strategyId,
  strategyName,
  configCount,
  onClose,
  onDeleted,
}: DeleteStrategyModalProps) {
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useDeleteUserStrategy();

  const handleDelete = async () => {
    setError(null);
    try {
      await mutateAsync(strategyId);
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete strategy');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Strategy" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13.5, color: '#ddd' }}>
          Delete <strong>{strategyName}</strong>? This cannot be undone from the UI.
        </p>

        {configCount > 0 && (
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(250,204,21,0.10)',
              border: '1px solid rgba(250,204,21,0.30)',
              borderRadius: 6,
              fontSize: 12.5,
              color: '#facc15',
            }}
          >
            This strategy is used by {configCount} {configCount === 1 ? 'configuration' : 'configurations'}.
            Deleting it will also remove those configurations and any backtest runs, grid searches,
            or portfolios that depend on them.
          </div>
        )}

        {error && (
          <div style={{ fontSize: 12.5, color: '#f87171' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 14px',
              background: 'none',
              border: '1px solid #444',
              borderRadius: 6,
              color: '#aaa',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isPending}
            style={{
              padding: '7px 16px',
              background: '#c0392b',
              border: 'none',
              borderRadius: 6,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
